import { useState } from 'react';
import { useAtom } from "jotai";
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { authenticationAtom } from '../State';
import { useModalContext } from '../components/ModalProvider';
import { useAdministrationAPI } from '../AdministrationAPI';
import Section from '../components/Section';
import ProfileDevicesSection from './ProfileDevicesSection';
import ProfileNotificationPreferences from './ProfileNotificationPreferences';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';

interface Properties {
}

// Wie im Bestand sind Vor-/Nachname nicht verpflichtend — das Schema
// validiert nur den Typ, damit sich das Speicherverhalten nicht ändert.
const profileSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

export default function UserProfilePanel(_properties: Properties) {
    const { t } = useTranslation();
    const { pushError } = useModalContext() as any;
    const { updateUser: updateUserAPI } = useAdministrationAPI();
    const [authentication, setAuthentication] = useAtom(authenticationAtom) as any;
    const [userProfileDialogOpen, setUserProfileDialogOpen] = useState(false);

    const form = useForm<ProfileFormValues>({
        resolver: zodResolver(profileSchema),
        defaultValues: {
            firstName: authentication?.firstName || '',
            lastName: authentication?.lastName || '',
        },
    });

    const openUserProfileDialog = () => {
        form.reset({
            firstName: authentication?.firstName || '',
            lastName: authentication?.lastName || '',
        });
        setUserProfileDialogOpen(true);
    };

    const updateUser = async (user: ProfileFormValues) => {
        const updatedUser = { ...authentication, ...user };

        try {
            const result = await updateUserAPI(updatedUser.customer, updatedUser);

            // updateUser is the one AdministrationAPI call that does not go through
            // processResponse: it returns the parsed body as-is, so a rejection
            // (e.g. 401) arrives as an `error` field instead of a thrown error.
            if (result?.error) {
                throw new Error(result.error);
            }

            setAuthentication(updatedUser);
            toast.success(t('settingsPanel.userProfilePanel.profileSaved'));
        } catch (error) {
            pushError(error);
        }
    };

    const submitProfile = (values: ProfileFormValues) => {
        updateUser(values);
        setUserProfileDialogOpen(false);
    };

    return <>
        <div className="flex w-full min-w-0 max-w-2xl flex-col gap-6">
            <Section title={t('settingsPanel.userProfilePanel.personalInformation')}>
                <div className="flex flex-wrap items-start gap-4">
                    <dl className="grid min-w-[240px] flex-1 grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
                        <dt className="font-semibold">{t('settingsPanel.userProfilePanel.firstName')}</dt>
                        <dd className="min-w-0 break-words">{authentication.firstName}</dd>
                        <dt className="font-semibold">{t('settingsPanel.userProfilePanel.lastName')}</dt>
                        <dd className="min-w-0 break-words">{authentication.lastName}</dd>
                        <dt className="font-semibold">{t('settingsPanel.userProfilePanel.email')}</dt>
                        <dd className="min-w-0 break-words">{authentication.email}</dd>
                    </dl>
                    <Button data-guide-id="settings.userProfile.edit" variant="outline" onClick={openUserProfileDialog}>
                        {t('global.edit')}
                    </Button>
                </div>
            </Section>
            <ProfileDevicesSection />
            <ProfileNotificationPreferences />
        </div>
        <Dialog open={userProfileDialogOpen} onOpenChange={setUserProfileDialogOpen}>
            <DialogContent
                className="max-w-[calc(100vw-2rem)] sm:max-w-[600px]"
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('settingsPanel.userProfilePanel.userProfile')}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(submitProfile)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="firstName"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('settingsPanel.userProfilePanel.firstName')}</FormLabel>
                                    <FormControl>
                                        <Input autoComplete="given-name" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="lastName"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('settingsPanel.userProfilePanel.lastName')}</FormLabel>
                                    <FormControl>
                                        <Input autoComplete="family-name" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <DialogFooter className="gap-2 pt-4 sm:gap-0">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setUserProfileDialogOpen(false)}
                            >
                                {t('global.cancel')}
                            </Button>
                            <Button type="submit">{t('global.apply')}</Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    </>;
}
